// Phase 2.7 — Merchants directory filter bar + sortable table extracted
// from Merchants.jsx. Pure presentational; parent owns all state + action
// callbacks. Inline row action menu (grant access, deactivate/reactivate,
// edit, delete) lives here because each button needs context from the row.

import { Btn, Section, Select, Badge } from "@/components/Shared";
import {
  Pencil, Trash2, ArrowUp, ArrowDown, ArrowUpDown,
  KeyRound, PowerOff, Power, TrendingUp, TrendingDown,
} from "lucide-react";
import { fmtBps } from "@/lib/api";

// Tiny ▲/▼ pill rendered next to the merchant name. Compares last
// complete month vs the month before. Hidden when prior period was zero
// (no baseline to grow from) or when the merchant is deactivated
// (irrelevant). Mid-aware tooltip so the operator can verify the numbers.
function VolumeDeltaBadge({ stats, currentPeriod, priorPeriod }) {
  if (!stats || stats.delta_pct == null) return null;
  const d = stats.delta_pct;
  if (Math.abs(d) < 0.05) return null;  // hide near-zero noise
  const up = d > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const cls = up
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-red-50 text-red-700 border-red-200";
  const fmtCr = (n) => `₹${(n / 1e7).toFixed(2)} Cr`;
  return (
    <span
      title={`${currentPeriod}: ${fmtCr(stats.current)}  vs  ${priorPeriod}: ${fmtCr(stats.prior)}`}
      className={`ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-sm border ${cls}`}
      data-testid="volume-delta-badge"
    >
      <Icon size={10} strokeWidth={2.5} />
      {up ? "+" : ""}{d.toFixed(1)}%
    </span>
  );
}

function SortHeader({ k, children, align = "left", sortBy, onToggle }) {
  const active = sortBy.key === k;
  const Icon = !active ? ArrowUpDown : (sortBy.dir === "asc" ? ArrowUp : ArrowDown);
  return (
    <th className={align === "right" ? "text-right" : ""}>
      <button
        type="button"
        data-testid={`sort-${k}`}
        onClick={() => onToggle(k)}
        className={`inline-flex items-center gap-1 hover:text-[var(--ink)] ${active ? "text-[var(--ink)]" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}
      >
        <span>{children}</span>
        <Icon size={11} strokeWidth={2} className={active ? "text-[var(--accent)]" : "text-[var(--muted)]"} />
      </button>
    </th>
  );
}

function renderShare(p) {
  if (!p) return <span className="text-[var(--muted)]">—</span>;
  const v = Number(p.partner_share_value || 0);
  const label = p.commission_structure === "Basis Points" ? "of vol" : "of rev";
  return <span className="num text-[12px]">{(v * 100).toFixed(2)}% <span className="text-[10px] text-[var(--muted)]">{label}</span></span>;
}

export default function MerchantsTable({
  // Filter state
  filterGateway, setFilterGateway,
  filterLob, setFilterLob,
  filterPartner, setFilterPartner,
  filterStatus, setFilterStatus,
  hasActiveFilters, onClearFilters,
  // Look-ups
  gateways, lobs, partners, statusOptions,
  partnerName,
  // Table data
  filtered, programByMid,
  taggedByMerchant,
  volumeDeltas,
  sortBy, onToggleSort,
  // Selection
  selectedIds, allVisibleSelected,
  onToggleSelectAll, onToggleSelected,
  // Row actions
  merchantHasAccess,
  onGrant, onReactivate, onDeactivate, onEdit, onRemove,
}) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="merchant-filter-bar">
        <div className="w-40"><Select testid="filter-gateway" placeholder="All gateways" options={gateways.map((g) => g.gateway_name)} value={filterGateway} onChange={setFilterGateway} /></div>
        <div className="w-44"><Select testid="filter-lob" placeholder="All LOBs" options={lobs || []} value={filterLob} onChange={setFilterLob} /></div>
        <div className="w-48"><Select testid="filter-partner" placeholder="All partners" options={partners.filter((p) => p.partner_id !== "P000").sort((a, b) => a.partner_id.localeCompare(b.partner_id)).map((p) => ({ value: p.partner_id, label: `${p.partner_id} · ${p.partner_name}` }))} value={filterPartner} onChange={setFilterPartner} /></div>
        <div className="w-44"><Select testid="filter-status" placeholder="All statuses" options={statusOptions} value={filterStatus} onChange={setFilterStatus} /></div>
        {hasActiveFilters && <Btn variant="ghost" testid="clear-filters" onClick={onClearFilters}>Clear filters</Btn>}
      </div>
      <Section title={`${filtered.length} merchant${filtered.length === 1 ? "" : "s"}${hasActiveFilters ? " · filtered" : ""}`} testid="merchants-section">
        <div className="overflow-x-auto"><table className="table-data w-full">
          <thead><tr>
            <th className="w-8"><input type="checkbox" checked={allVisibleSelected} onChange={onToggleSelectAll} data-testid="select-all-merchants" className="cursor-pointer" /></th>
            <SortHeader k="merchant_id"   sortBy={sortBy} onToggle={onToggleSort}>Merchant ID</SortHeader>
            <SortHeader k="merchant_name" sortBy={sortBy} onToggle={onToggleSort}>Name</SortHeader>
            <SortHeader k="gateway"       sortBy={sortBy} onToggle={onToggleSort}>Gateway</SortHeader>
            <SortHeader k="lob"           sortBy={sortBy} onToggle={onToggleSort}>LOB</SortHeader>
            <SortHeader k="base_rate"     sortBy={sortBy} onToggle={onToggleSort} align="right">Base</SortHeader>
            <SortHeader k="merchant_rate" sortBy={sortBy} onToggle={onToggleSort} align="right">Billing</SortHeader>
            <SortHeader k="partner"       sortBy={sortBy} onToggle={onToggleSort}>Partner</SortHeader>
            <th>Structure</th>
            <SortHeader k="share"            sortBy={sortBy} onToggle={onToggleSort} align="right">Share</SortHeader>
            <SortHeader k="settlement_type"  sortBy={sortBy} onToggle={onToggleSort}>Settlement</SortHeader>
            <SortHeader k="status"           sortBy={sortBy} onToggle={onToggleSort}>Status</SortHeader>
            <th></th>
          </tr></thead>
          <tbody>
            {filtered.map((r) => {
              const prog = programByMid[r.merchant_id];
              const tagged = Array.from(taggedByMerchant[r.merchant_id] || []);
              if (r.partner_id && r.partner_id !== "P000" && !tagged.includes(r.partner_id)) tagged.push(r.partner_id);
              const primaryPartner = prog?.referred_partner_id || r.partner_id;
              const secPid = prog?.secondary_partner_id || r.secondary_partner_id;
              const secVal = prog?.secondary_share_value ?? r.secondary_share_value;
              const hasPrimary = primaryPartner && primaryPartner !== "P000";
              const hasSecondary = secPid && secPid !== "P000" && Number(secVal);
              const st = r.status || "Active";
              return (
                <tr key={r.merchant_id} data-testid={`merchant-row-${r.merchant_id}`}>
                  <td><input type="checkbox" checked={selectedIds.has(r.merchant_id)} onChange={() => onToggleSelected(r.merchant_id)} data-testid={`select-merchant-${r.merchant_id}`} className="cursor-pointer" /></td>
                  <td className="num">{r.merchant_id}</td>
                  <td className="font-medium">
                    {r.merchant_name}
                    {r.status !== "Deactivated" && (
                      <VolumeDeltaBadge
                        stats={volumeDeltas?.by_merchant?.[r.merchant_id]}
                        currentPeriod={volumeDeltas?.current_period}
                        priorPeriod={volumeDeltas?.prior_period}
                      />
                    )}
                    <div className="text-[11px] text-[var(--muted)] truncate max-w-[180px]">{r.notes || ""}</div>
                  </td>
                  <td>{r.gateway || prog?.gateway || <span className="text-[var(--muted)]">—</span>}</td>
                  <td className="text-[12px]">{r.lob || prog?.lob || <span className="text-[var(--muted)]">—</span>}</td>
                  <td className="text-right num">{prog?.base_rate != null ? fmtBps(prog.base_rate) : <span className="text-[var(--muted)]">—</span>}</td>
                  <td className="text-right num">{prog?.merchant_rate != null ? fmtBps(prog.merchant_rate) : (r.merchant_rate != null ? fmtBps(r.merchant_rate) : <span className="text-[var(--muted)]">—</span>)}</td>
                  <td className="text-[12px]">
                    {hasPrimary ? partnerName(primaryPartner) : <span className="text-[var(--muted)]">—</span>}
                    {hasSecondary ? (
                      <div className="flex items-center gap-1.5 mt-1" title={`Secondary partner — ${(Number(secVal) * 100).toFixed(2)}% of gross, paid on top`}>
                        <span className="inline-block w-1 h-1 bg-[var(--accent)] rounded-full" />
                        <span className="text-[11px] text-[var(--accent)] font-semibold">{partnerName(secPid)}</span>
                        <span className="text-[10px] text-[var(--muted)] num">+{(Number(secVal) * 100).toFixed(2)}%</span>
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {(!prog || (!hasPrimary && !hasSecondary))
                      ? <span className="text-[var(--muted)] text-[11px]">—</span>
                      : (prog.commission_structure === "Basis Points"
                          ? <Badge tone="accent">% of Vol</Badge>
                          : <Badge tone="muted">% of Rev</Badge>)}
                  </td>
                  <td className="text-right">
                    {(!hasPrimary && !hasSecondary)
                      ? <span className="text-[var(--muted)]">—</span>
                      : renderShare(prog)}
                  </td>
                  <td>{r.settlement_type ? <Badge tone={r.settlement_type === "IS" ? "accent" : "muted"}>{r.settlement_type}</Badge> : <span className="text-[var(--muted)] text-[11px]">—</span>}</td>
                  <td>
                    {st === "Deactivated" ? (
                      <Badge tone="warning" testid={`status-${r.merchant_id}`}>
                        Deactivated · {r.deactivation_date ? String(r.deactivation_date).slice(0, 10) : "?"}
                      </Badge>
                    ) : (
                      <Badge tone={st === "Active" ? "positive" : (st === "Inactive" ? "muted" : "warning")}>{st}</Badge>
                    )}
                  </td>
                  <td className="text-right">
                    {merchantHasAccess(r) ? (
                      <button onClick={() => onGrant(r)} title={`${r.merchant_name} already has portal access — click to view / re-issue`} className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm text-emerald-600" data-testid={`grant-access-${r.merchant_id}`}><KeyRound size={13} /></button>
                    ) : (
                      <button onClick={() => onGrant(r)} title="Generate portal access for this merchant" className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm text-[var(--accent)]" data-testid={`grant-access-${r.merchant_id}`}><KeyRound size={13} /></button>
                    )}
                    {st === "Deactivated" ? (
                      <button onClick={() => onReactivate(r)} title={`Re-activate ${r.merchant_id}`} className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm text-emerald-700" data-testid={`reactivate-${r.merchant_id}`}><Power size={13} /></button>
                    ) : (
                      <button onClick={() => onDeactivate(r)} title={`Mark ${r.merchant_id} as no-longer-transacting`} className="p-1.5 hover:bg-amber-50 rounded-sm text-amber-700" data-testid={`deactivate-${r.merchant_id}`}><PowerOff size={13} /></button>
                    )}
                    <button onClick={() => onEdit(r)} className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm" data-testid={`edit-merchant-${r.merchant_id}`}><Pencil size={13} /></button>
                    <button onClick={() => onRemove(r.merchant_id)} className="p-1.5 hover:bg-red-50 text-[var(--negative)] rounded-sm" data-testid={`delete-merchant-${r.merchant_id}`}><Trash2 size={13} /></button>
                  </td>
                </tr>
              );
            })}
            {!filtered.length && <tr><td colSpan={13} className="text-center text-[var(--muted)] py-8">{hasActiveFilters ? "No merchants match the current filters." : "No merchants yet."}</td></tr>}
          </tbody>
        </table></div>
      </Section>
    </>
  );
}
